/**
 * The verification UI — the heart of M4.
 *
 * A pack, its coverage, and the queue of things a human still has to check
 * against the BZO. Everything below the header is a row that opens into the
 * side-by-side: what was read on the left, the passage it came from on the
 * right, and the controls that record a decision sitting under that passage.
 *
 * What is deliberately NOT here: any control that confirms more than one zone
 * row or more than one rule. The brief allows bulk confirmation per zone row
 * and refuses it for a whole pack; the backend refuses it too (422), and a test
 * asserts the affordance does not exist here — a promise the UI keeps by not
 * offering the button, not by hoping nobody presses it.
 *
 * Who may decide (Founder decision 2026-09-16): the founder. A confirmed cell
 * turns into a hard finding for the workspace that confirmed it, so the person
 * who sets `verified` is Velox, not the prospect. Every other session gets the
 * same screen read-only — every row, every passage, the export — with one
 * sentence saying so. The backend refuses the same session with a 403, so the
 * read-only rendering is a courtesy, not the gate.
 */
import { ArrowLeft, Download } from 'lucide-react'
import { useState } from 'react'

import { api, packExportHref } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { LoadingBlock } from '@/components/LoadingBlock'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useSession } from '@/features/auth/SessionContext'
import { useI18n } from '@/i18n'
import { buildPath, navigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

import { CoverageMeter } from './CoverageMeter'
import { DraftBadge, DraftNote } from './DraftBadge'
import { RuleCard } from './RuleCard'
import { ShadowedNote } from './ShadowedNote'
import { ZoneRowCard } from './ZoneRowCard'
import { joinPackId } from './rulebase'
import type { DraftPack, WorkspaceRef } from '@/api/types'

export function VerifyPackScreen({
  workspace,
  kanton,
  gemeinde,
}: {
  workspace: WorkspaceRef
  kanton: string
  gemeinde: string
}) {
  const { t } = useI18n()
  const { isFounder, status: sessionStatus } = useSession()
  // Read-only until proven founder: while the session is still loading, a
  // control that would vanish a moment later must not flash into view.
  const readOnly = !isFounder
  const packId = joinPackId(kanton, gemeinde)
  const loaded = useAsync<DraftPack>(
    (signal) => api.draftPack(workspace.id, packId, signal),
    [workspace.id, packId],
  )
  /**
   * Every verify call answers with the whole updated pack, so one round trip
   * refreshes the coverage AND the row. This local override is what makes the
   * counters move the moment a row is committed instead of on a reload.
   */
  const [updated, setUpdated] = useState<DraftPack | null>(null)
  const pack = updated ?? loaded.data ?? null

  if (loaded.loading && !pack) {
    return <LoadingBlock />
  }

  if (loaded.error && !pack) {
    return (
      <div className="flex flex-col gap-4">
        <ErrorNote message={loaded.error} onRetry={loaded.reload} retryLabel={t('common.retry')} />
        <p className="text-[13px] text-muted">{t('verify.packNotFound')}</p>
      </div>
    )
  }

  if (!pack) return <p className="text-[13px] text-muted">{t('verify.packNotFound')}</p>

  const draft = pack.draft
  const coverage = pack.coverage
  const zones = pack.zones ?? []
  const rules = pack.rules ?? []

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => navigate(buildPath('rulebase', { workspaceId: workspace.id }))}
        >
          <Icon icon={ArrowLeft} />
          {t('common.back')}
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-[32px] leading-tight font-semibold">
            {pack.gemeinde} <span className="text-muted-2">({pack.kanton})</span>
          </h1>
          {draft ? <DraftBadge /> : null}
        </div>
        <code className="num text-[13px] text-muted-2">{pack.pack_id}</code>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('verify.lead')}</p>
      </section>

      {draft ? <DraftNote gemeinde={pack.gemeinde} /> : null}
      {pack.shadowed ? <ShadowedNote conflicts={pack.conflicts ?? []} /> : null}
      {readOnly && sessionStatus !== 'loading' ? (
        <p
          role="note"
          className="max-w-[70ch] rounded-md border border-border bg-surface px-4 py-3 text-[13px] text-ink-2"
          data-testid="verification-founder-only"
        >
          {t('verify.founderOnlyNote')}
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('start.gemeindenTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <CoverageMeter
            label={t('rulebase.zones')}
            counts={coverage.zones}
            testId="coverage-zones"
          />
          <CoverageMeter
            label={t('rulebase.zoneParameters')}
            counts={coverage.zone_parameters}
            testId="coverage-zone-parameters"
          />
          <CoverageMeter
            label={t('rulebase.rules')}
            counts={coverage.rules}
            testId="coverage-rules"
          />
        </CardContent>
      </Card>

      {/* Said once, in the open: the missing button is the feature. */}
      <p className="max-w-[70ch] text-[12px] text-muted" data-testid="no-whole-pack-note">
        {t('verify.noWholePack')}
      </p>

      <section aria-labelledby="verify-zones" className="flex flex-col gap-4">
        <div>
          <h2 id="verify-zones" className="font-display text-[21px] font-semibold">
            {t('verify.zonesTitle')}
          </h2>
          <p className="mt-1 max-w-[70ch] text-[13px] text-muted">{t('verify.zonesLead')}</p>
        </div>
        {zones.length === 0 ? (
          <p className="text-[13px] text-muted">{t('verify.zonesEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {zones.map((zone) => (
              <ZoneRowCard
                key={zone.id}
                workspaceId={workspace.id}
                packId={pack.pack_id}
                zone={zone}
                onUpdated={setUpdated}
                readOnly={readOnly}
              />
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="verify-rules" className="flex flex-col gap-4">
        <div>
          <h2 id="verify-rules" className="font-display text-[21px] font-semibold">
            {t('verify.rulesTitle')}
          </h2>
          <p className="mt-1 max-w-[70ch] text-[13px] text-muted">{t('verify.rulesLead')}</p>
        </div>
        {rules.length === 0 ? (
          <p className="text-[13px] text-muted">{t('verify.rulesEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {rules.map((rule) => (
              <RuleCard
                key={rule.id}
                workspaceId={workspace.id}
                packId={pack.pack_id}
                rule={rule}
                onUpdated={setUpdated}
                readOnly={readOnly}
              />
            ))}
          </ul>
        )}
      </section>

      {/* The pack's way into the repository: a YAML the founder commits through
          the ordinary git flow. The server never writes into the repo tree, so
          the diff stays reviewable — which is what keeps rules data. */}
      <section className="flex flex-col gap-2">
        <a
          href={packExportHref(workspace.id, pack.pack_id, 'yaml')}
          className="inline-flex w-fit items-center gap-2 rounded-sm border border-border-strong bg-white px-3 py-1.5 text-[13px] font-medium text-ink hover:bg-surface"
        >
          <Icon icon={Download} />
          {t('verify.exportCta')}
        </a>
        <p className="max-w-[70ch] text-[12px] text-muted">{t('verify.exportHint')}</p>
      </section>
    </div>
  )
}
